import { useEffect, useRef } from 'react';
import { useSignalR } from '../providers/SignalR';

/* The net under SignalR — and it is a net, not a second source of truth. */
const CONNECTED_BEAT_MS = 30_000;
// Quick at first so a short drop heals fast, then slower so a long outage does not hammer heavy reads.
const DISCONNECTED_BEATS_MS = [6_000, 12_000, 20_000];

/* Repeat a live-update refresh for the events that do not arrive. */
export function useMissedEventBeat(refresh: () => void, enabled = true): void {
  const { isConnected } = useSignalR() as { isConnected?: boolean };

  // Back after a drop: what was sent meanwhile is gone, so read it now rather than at the next beat.
  const droppedRef = useRef(false);
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    if (isConnected) {
      if (droppedRef.current && enabled) refresh();
      droppedRef.current = false;
    } else if (wasConnectedRef.current) {
      droppedRef.current = true;
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    if (!enabled) return undefined;

    let beats = 0;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const wait = isConnected
        ? CONNECTED_BEAT_MS
        : DISCONNECTED_BEATS_MS[Math.min(beats, DISCONNECTED_BEATS_MS.length - 1)];
      timer = setTimeout(() => {
        if (document.visibilityState !== 'hidden') refresh();
        beats += 1;
        schedule();
      }, wait);
    };
    schedule();
    // No refresh on coming back to the tab: a connection that held missed nothing, and one that dropped refreshes on reconnect.
    return () => clearTimeout(timer);
  }, [refresh, enabled, isConnected]);
}
